/**
 * Cost arithmetic for container block-storage volumes.
 *
 * Volumes are provisioned and displayed in GiB but billed per decimal GB, so every figure shown
 * to a user has to go through the conversion below. This mirrors the backend exactly
 * (xver-api `billableCost`): size_gib x 1.073741824 x rate. Keep the two in step.
 */

/** 1 GiB = 1024^3 bytes, 1 GB = 1000^3 bytes. */
export const GIB_TO_GB = 1.073741824;

/** Matches xver-api's DefaultVolumeGBMonthlyRate; used only when the API gives us nothing. */
export const DEFAULT_VOLUME_GB_MONTHLY_RATE = 4.0;

/**
 * The pricing type declares the rate as required, but an API deployed from an older build will
 * omit it at runtime — and `size * undefined` renders as "NaN ฿". Always read the rate through
 * this.
 */
export function resolveVolumeRate(rate: number | undefined | null): number {
    return typeof rate === 'number' && Number.isFinite(rate) && rate >= 0
        ? rate
        : DEFAULT_VOLUME_GB_MONTHLY_RATE;
}

/** Billable size of a volume, in decimal GB. */
export function volumeGB(sizeGiB: number): number {
    if (!Number.isFinite(sizeGiB) || sizeGiB <= 0) return 0;
    return sizeGiB * GIB_TO_GB;
}

export function volumeMonthlyCost(sizeGiB: number, rate: number | undefined): number {
    return volumeGB(sizeGiB) * resolveVolumeRate(rate);
}

export function totalMonthlyCost(sizesGiB: number[], rate: number | undefined): number {
    const resolved = resolveVolumeRate(rate);
    return sizesGiB.reduce((sum, size) => sum + volumeGB(size) * resolved, 0);
}

export function totalGiB(sizesGiB: number[]): number {
    return sizesGiB.reduce((sum, size) => sum + (Number.isFinite(size) ? size : 0), 0);
}

/** Matches PricingSummary.formatBaht: Thai locale, always two decimals. */
export function formatBaht(value: number): string {
    return value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
